import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import { isVideoMedia } from 'core/functions';
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	CloseIcon,
	FullscreenExitIcon,
	FullscreenIcon,
	MiniPlayBadgeIcon,
	PauseIcon,
	PlayIcon,
	VolumeOffIcon,
	VolumeUpIcon,
} from './MediaPreviewPopup.icons';
import { MediaPreviewInput, MediaPreviewItem, MediaPreviewPopupProps, ResolvedMedia } from './MediaPreviewPopup.types';
import { formatTime } from './MediaPreviewPopup.utils';
import styles from './MediaPreviewPopup.module.scss';

export * from './MediaPreviewPopup.types';

export const MediaPreviewPopup: FC<MediaPreviewPopupProps> = ({ open, onClose, items, initialIndex = 0 }) => {
	const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);
	const [isPlaying, setIsPlaying] = useState<boolean>(true);
	const [currentTime, setCurrentTime] = useState<number>(0);
	const [duration, setDuration] = useState<number>(0);
	const [isMuted, setIsMuted] = useState<boolean>(false);
	const [showControls, setShowControls] = useState<boolean>(true);
	const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

	const videoRef = useRef<HTMLVideoElement>(null);
	const stageRef = useRef<HTMLDivElement>(null);
	const seekFillRef = useRef<HTMLDivElement>(null);
	const seekThumbRef = useRef<HTMLDivElement>(null);
	const animFrameRef = useRef<number | null>(null);
	const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const blobUrlsRef = useRef<Map<File, string>>(new Map());

	// 60fps smooth progress bar animation
	useEffect(() => {
		if (!isPlaying || !videoRef.current) {
			if (animFrameRef.current) {
				cancelAnimationFrame(animFrameRef.current);
				animFrameRef.current = null;
			}
			return;
		}

		const updateSmoothProgress = () => {
			if (videoRef.current && duration > 0) {
				const cur = videoRef.current.currentTime;
				const pct = Math.min(100, Math.max(0, (cur / duration) * 100));
				if (seekFillRef.current) {
					seekFillRef.current.style.width = `${pct}%`;
				}
				if (seekThumbRef.current) {
					seekThumbRef.current.style.left = `${pct}%`;
				}
			}
			animFrameRef.current = requestAnimationFrame(updateSmoothProgress);
		};

		animFrameRef.current = requestAnimationFrame(updateSmoothProgress);

		return () => {
			if (animFrameRef.current) {
				cancelAnimationFrame(animFrameRef.current);
				animFrameRef.current = null;
			}
		};
	}, [isPlaying, duration]);

	useEffect(() => {
		const onFullscreenChange = () => {
			const doc = document as any;
			const isCurrentFs = !!(
				doc.fullscreenElement ||
				doc.webkitFullscreenElement ||
				doc.mozFullScreenElement ||
				doc.msFullscreenElement
			);
			setIsFullscreen(isCurrentFs);
		};

		document.addEventListener('fullscreenchange', onFullscreenChange);
		document.addEventListener('webkitfullscreenchange', onFullscreenChange);
		document.addEventListener('mozfullscreenchange', onFullscreenChange);
		document.addEventListener('MSFullscreenChange', onFullscreenChange);

		return () => {
			document.removeEventListener('fullscreenchange', onFullscreenChange);
			document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
			document.removeEventListener('mozfullscreenchange', onFullscreenChange);
			document.removeEventListener('MSFullscreenChange', onFullscreenChange);
		};
	}, []);

	useEffect(() => {
		if (open) {
			const safeIndex = Math.max(0, Math.min(items.length - 1, initialIndex));
			setCurrentIndex(safeIndex);
			setIsPlaying(true);
			setCurrentTime(0);
			setDuration(0);
			setShowControls(true);
			if (seekFillRef.current) {
				seekFillRef.current.style.width = '0%';
			}
			if (seekThumbRef.current) {
				seekThumbRef.current.style.left = '0%';
			}
		}
	}, [open, initialIndex, items.length]);

	const resolvedItems = useMemo<ResolvedMedia[]>(() => {
		const currentFiles = new Set(items.filter((item): item is File => item instanceof File));

		// Revoke and delete object URLs for files that were removed
		blobUrlsRef.current.forEach((url, file) => {
			if (!currentFiles.has(file)) {
				URL.revokeObjectURL(url);
				blobUrlsRef.current.delete(file);
			}
		});

		return items.map(item => {
			if (item instanceof File) {
				const isVideo = isVideoMedia(item);
				if (!blobUrlsRef.current.has(item)) {
					blobUrlsRef.current.set(item, URL.createObjectURL(item));
				}
				const url = blobUrlsRef.current.get(item)!;
				return { url, isVideo, name: item.name, isTempBlob: true };
			}

			if (typeof item === 'string') {
				const isVideo = isVideoMedia(item);
				const isFullUrl =
					item.startsWith('blob:') ||
					item.startsWith('http://') ||
					item.startsWith('https://') ||
					item.startsWith('data:');
				const url = isFullUrl ? item : `${process.env.REACT_APP_S3_API}/${item}`;
				return { url, isVideo, isTempBlob: false };
			}

			const isVideo = item.type === 'video' || isVideoMedia(item.url) || isVideoMedia(item.name || '');
			const isFullUrl =
				item.url.startsWith('blob:') ||
				item.url.startsWith('http://') ||
				item.url.startsWith('https://') ||
				item.url.startsWith('data:');
			const url = isFullUrl ? item.url : `${process.env.REACT_APP_S3_API}/${item.url}`;
			return { url, isVideo, name: item.name, isTempBlob: false };
		});
	}, [items]);

	useEffect(() => {
		return () => {
			if (controlsTimeoutRef.current) {
				clearTimeout(controlsTimeoutRef.current);
				controlsTimeoutRef.current = null;
			}
			if (videoRef.current) {
				videoRef.current.pause();
				videoRef.current.removeAttribute('src');
				videoRef.current.load();
			}
			blobUrlsRef.current.forEach(url => {
				URL.revokeObjectURL(url);
			});
			blobUrlsRef.current.clear();
		};
	}, []);

	const totalItems = resolvedItems.length;
	const currentMedia = resolvedItems[currentIndex];

	// Programmatic Autoplay
	useEffect(() => {
		if (open && currentMedia?.isVideo && videoRef.current) {
			videoRef.current.currentTime = 0;
			const playPromise = videoRef.current.play();
			if (playPromise !== undefined) {
				playPromise
					.then(() => setIsPlaying(true))
					.catch(() => {
						if (videoRef.current) {
							videoRef.current.muted = true;
							setIsMuted(true);
							videoRef.current
								.play()
								.then(() => setIsPlaying(true))
								.catch(() => setIsPlaying(false));
						}
					});
			}
		}
	}, [open, currentIndex, currentMedia?.isVideo]);

	const handlePrev = useCallback(() => {
		if (totalItems <= 1 || currentIndex <= 0) return;
		videoRef.current?.pause();
		setCurrentIndex(prev => prev - 1);
	}, [totalItems, currentIndex]);

	const handleNext = useCallback(() => {
		if (totalItems <= 1 || currentIndex >= totalItems - 1) return;
		videoRef.current?.pause();
		setCurrentIndex(prev => prev + 1);
	}, [totalItems, currentIndex]);

	const handleClose = useCallback(() => {
		if (controlsTimeoutRef.current) {
			clearTimeout(controlsTimeoutRef.current);
			controlsTimeoutRef.current = null;
		}
		if (videoRef.current) {
			videoRef.current.pause();
			videoRef.current.removeAttribute('src');
			videoRef.current.load();
		}
		const doc = document as any;
		if (doc.fullscreenElement || doc.webkitFullscreenElement) {
			if (doc.exitFullscreen) {
				doc.exitFullscreen().catch(() => {});
			} else if (doc.webkitExitFullscreen) {
				doc.webkitExitFullscreen();
			}
		}
		onClose();
	}, [onClose]);

	// Video controls logic
	const togglePlay = useCallback(() => {
		if (!videoRef.current) return;
		if (videoRef.current.paused) {
			videoRef.current.play();
			setIsPlaying(true);
		} else {
			videoRef.current.pause();
			setIsPlaying(false);
		}
	}, []);

	const toggleMute = useCallback(() => {
		if (!videoRef.current) return;
		videoRef.current.muted = !videoRef.current.muted;
		setIsMuted(videoRef.current.muted);
	}, []);

	const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!videoRef.current || !duration) return;
		const rect = e.currentTarget.getBoundingClientRect();
		const clickX = e.clientX - rect.left;
		const newRatio = Math.max(0, Math.min(1, clickX / rect.width));
		const newTime = newRatio * duration;
		videoRef.current.currentTime = newTime;
		setCurrentTime(newTime);
		const pct = newRatio * 100;
		if (seekFillRef.current) {
			seekFillRef.current.style.width = `${pct}%`;
		}
		if (seekThumbRef.current) {
			seekThumbRef.current.style.left = `${pct}%`;
		}
	};

	const toggleFullscreen = useCallback(() => {
		if (!stageRef.current) return;
		const doc = document as any;
		const el = stageRef.current as any;

		const isFs = !!(
			doc.fullscreenElement ||
			doc.webkitFullscreenElement ||
			doc.mozFullScreenElement ||
			doc.msFullscreenElement
		);

		if (!isFs) {
			if (el.requestFullscreen) {
				el.requestFullscreen().catch(() => {});
			} else if (el.webkitRequestFullscreen) {
				el.webkitRequestFullscreen();
			} else if (el.mozRequestFullScreen) {
				el.mozRequestFullScreen();
			} else if (el.msRequestFullscreen) {
				el.msRequestFullscreen();
			}
		} else if (doc.exitFullscreen) {
			doc.exitFullscreen().catch(() => {});
		} else if (doc.webkitExitFullscreen) {
			doc.webkitExitFullscreen();
		} else if (doc.mozCancelFullScreen) {
			doc.mozCancelFullScreen();
		} else if (doc.msExitFullscreen) {
			doc.msExitFullscreen();
		}
	}, []);

	const handleMouseMove = () => {
		setShowControls(true);
		if (controlsTimeoutRef.current) {
			clearTimeout(controlsTimeoutRef.current);
		}
		if (isPlaying) {
			controlsTimeoutRef.current = setTimeout(() => {
				setShowControls(false);
			}, 2500);
		}
	};

	// Keyboard navigation
	useEffect(() => {
		if (!open) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				const doc = document as any;
				const isFs = !!(
					doc.fullscreenElement ||
					doc.webkitFullscreenElement ||
					doc.mozFullScreenElement ||
					doc.msFullscreenElement
				);
				if (isFs) {
					if (doc.exitFullscreen) {
						doc.exitFullscreen().catch(() => {});
					} else if (doc.webkitExitFullscreen) {
						doc.webkitExitFullscreen();
					}
				} else {
					handleClose();
				}
			} else if (e.key === 'ArrowLeft') {
				handlePrev();
			} else if (e.key === 'ArrowRight') {
				handleNext();
			} else if (e.key === ' ' && currentMedia?.isVideo) {
				e.preventDefault();
				togglePlay();
			} else if ((e.key === 'f' || e.key === 'F') && currentMedia?.isVideo) {
				e.preventDefault();
				toggleFullscreen();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [open, handleClose, handlePrev, handleNext, togglePlay, toggleFullscreen, currentMedia?.isVideo]);

	useEffect(() => {
		if (open) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [open]);

	if (!open || !currentMedia) return null;

	const seekProgressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

	return createPortal(
		<div
			className={styles.backdrop}
			role="dialog"
			aria-modal="true"
			onClick={e => {
				if (e.target === e.currentTarget) {
					handleClose();
				}
			}}
			onMouseMove={handleMouseMove}
		>
			{/* Floating Navigation Arrows */}
			{!isFullscreen && totalItems > 1 && currentIndex > 0 && (
				<button
					type="button"
					className={cx(styles.navButton, styles['navButton--prev'])}
					onClick={handlePrev}
					aria-label="Previous media"
				>
					<ChevronLeftIcon />
				</button>
			)}

			{!isFullscreen && totalItems > 1 && currentIndex < totalItems - 1 && (
				<button
					type="button"
					className={cx(styles.navButton, styles['navButton--next'])}
					onClick={handleNext}
					aria-label="Next media"
				>
					<ChevronRightIcon />
				</button>
			)}

			{/* Main Media Stage (hugging media dimensions without fixed black card) */}
			<div
				className={cx(styles.modalStage, {
					[styles['modalStage--fullscreen']]: isFullscreen,
				})}
				ref={stageRef}
			>
				<div
					className={cx(styles.mediaWrapper, {
						[styles['mediaWrapper--fullscreen']]: isFullscreen,
					})}
				>
					<button
						type="button"
						className={cx(styles.closeButton, {
							[styles['closeButton--fullscreen']]: isFullscreen,
							[styles['closeButton--hidden']]: isFullscreen && !showControls && isPlaying,
						})}
						onClick={handleClose}
						aria-label="Close media preview"
					>
						<CloseIcon />
					</button>

					{currentMedia.isVideo ? (
						<div
							className={cx(styles.videoContainer, {
								[styles['videoContainer--fullscreen']]: isFullscreen,
							})}
							onClick={togglePlay}
							onDoubleClick={e => {
								e.stopPropagation();
								toggleFullscreen();
							}}
							onMouseLeave={() => isPlaying && setShowControls(false)}
						>
							{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
							<video
								key={currentMedia.url}
								ref={videoRef}
								src={currentMedia.url}
								className={cx(styles.mediaVideo, {
									[styles['mediaVideo--fullscreen']]: isFullscreen,
								})}
								autoPlay
								playsInline
								onPlay={() => setIsPlaying(true)}
								onPause={() => setIsPlaying(false)}
								onTimeUpdate={() => {
									if (videoRef.current) {
										setCurrentTime(videoRef.current.currentTime);
									}
								}}
								onLoadedMetadata={() => {
									if (videoRef.current) {
										setDuration(videoRef.current.duration);
										setIsMuted(videoRef.current.muted);
									}
								}}
								onEnded={() => {
									setIsPlaying(false);
									if (seekFillRef.current) {
										seekFillRef.current.style.width = '100%';
									}
									if (seekThumbRef.current) {
										seekThumbRef.current.style.left = '100%';
									}
								}}
							/>

							{!isPlaying && (
								<div
									className={styles.centerPlayBtn}
									onClick={e => {
										e.stopPropagation();
										togglePlay();
									}}
									role="button"
									tabIndex={0}
									aria-label="Play video"
								>
									<PlayIcon />
								</div>
							)}

							<div
								className={cx(styles.videoControls, {
									[styles['videoControls--fullscreen']]: isFullscreen,
									[styles['videoControls--hidden']]: !showControls && isPlaying,
								})}
								onClick={e => e.stopPropagation()}
							>
								<button
									type="button"
									className={styles.controlBtn}
									onClick={togglePlay}
									aria-label={isPlaying ? 'Pause' : 'Play'}
								>
									{isPlaying ? <PauseIcon /> : <PlayIcon />}
								</button>

								<span className={styles.timeText}>
									{formatTime(currentTime)} / {formatTime(duration)}
								</span>

								<div className={styles.seekTrack} onClick={handleSeek}>
									<div
										ref={seekFillRef}
										className={styles.seekFill}
										style={{ width: `${Math.min(100, Math.max(0, seekProgressPercent))}%` }}
									/>
									<div
										ref={seekThumbRef}
										className={styles.seekThumb}
										style={{ left: `${Math.min(100, Math.max(0, seekProgressPercent))}%` }}
									/>
								</div>

								<button
									type="button"
									className={styles.controlBtn}
									onClick={toggleMute}
									aria-label={isMuted ? 'Unmute' : 'Mute'}
								>
									{isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
								</button>

								<button
									type="button"
									className={styles.controlBtn}
									onClick={toggleFullscreen}
									aria-label={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
								>
									{isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
								</button>
							</div>
						</div>
					) : (
						<img
							key={currentMedia.url}
							src={currentMedia.url}
							alt={currentMedia.name || 'media-preview'}
							className={cx(styles.mediaImage, {
								[styles['mediaImage--fullscreen']]: isFullscreen,
							})}
							draggable={false}
						/>
					)}
				</div>
			</div>

			{/* Floating Telegram-Style Thumbnail Dock */}
			{!isFullscreen && totalItems > 1 && (
				<div
					className={cx(styles.thumbnailDock, {
						[styles['thumbnailDock--hidden']]: !showControls && isPlaying,
					})}
				>
					{resolvedItems.map((item, idx) => (
						<div
							key={idx}
							className={cx(styles.thumbnailItem, {
								[styles['thumbnailItem--active']]: idx === currentIndex,
							})}
							onClick={() => {
								videoRef.current?.pause();
								setCurrentIndex(idx);
							}}
						>
							{item.isVideo ? (
								<>
									{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
									<video src={item.url} preload="metadata" muted />
									<div className={styles.thumbnailVideoBadge}>
										<MiniPlayBadgeIcon />
									</div>
								</>
							) : (
								<img src={item.url} alt={`thumb-${idx}`} draggable={false} />
							)}
						</div>
					))}
				</div>
			)}
		</div>,
		document.body
	);
};

export default MediaPreviewPopup;
